import React, { useState, useRef, useCallback } from "react"
import { Upload, X } from "lucide-react"
import toast from "react-hot-toast"
import { twMerge } from "tailwind-merge"

import { ConversationType } from "@src/types"
import Swal from "sweetalert2"

interface PhotoUploadProps {
  value?: string
  onChange: (photoDataUrl: string | null) => void
  className?: string
  disabled?: boolean
  onFileChange?: (file: File | null) => void
  hFull?: boolean | string
  mediaType?: ConversationType
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  value,
  onChange,
  className = "",
  disabled = false,
  onFileChange,
  hFull = false,
  mediaType = ConversationType.IMAGE,
}) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(
    (file: File) => {
      if (mediaType === ConversationType.IMAGE) {
        if (!file.type.startsWith("image/")) {
          Swal.fire({
            title: "Error",
            text: "Please select a valid image file",
            icon: "error",
          })
          return
        }
        if (file.size > 5 * 1024 * 1024) {
          // 5MB limit
          Swal.fire({
            title: "Error",
            text: "Image size must be less than 5MB",
            icon: "error",
          })
          return
        }
      } else if (mediaType === ConversationType.AUDIO) {
        if (!file.type.startsWith("audio/")) {
          Swal.fire({
            title: "Error",
            text: "Please select a valid audio file",
            icon: "error",
          })
          return
        }
        if (file.size > 50 * 1024 * 1024) {
          Swal.fire({
            title: "Error",
            text: "Audio size must be less than 50MB",
            icon: "error",
          })
          return
        }
      } else if (mediaType === ConversationType.VIDEO) {
        if (!file.type.startsWith("video/")) {
          Swal.fire({
            title: "Error",
            text: "Please select a valid video file",
            icon: "error",
          })
          return
        }
        if (file.size > 100 * 1024 * 1024) {
          Swal.fire({
            title: "Error",
            text: "Video size must be less than 100MB",
            icon: "error",
          })
          return
        }
      } else if (mediaType === ConversationType.TEXT) {
        if (!file.type.startsWith("text/")) {
          Swal.fire({
            title: "Error",
            text: "Please select a valid text file",
            icon: "error",
          })
          return
        }
        if (file.size > 50 * 1024 * 1024) {
          Swal.fire({
            title: "Error",
            text: "Text size must be less than 50MB",
            icon: "error",
          })
          return
        }
      }
      setIsUploading(true)
      const reader = new FileReader()
      reader.onload = (e) => {
        const result = e.target?.result as string
        onChange(result)
        setIsUploading(false)
      }
      reader.onerror = () => {
        toast.error("Error reading file")
        setIsUploading(false)
      }
      if (mediaType === ConversationType.IMAGE) {
        reader.readAsDataURL(file)
      } else if (mediaType === ConversationType.AUDIO) {
        const blob = new Blob([file], { type: file.type })
        reader.readAsDataURL(blob)
      } else if (mediaType === ConversationType.VIDEO) {
        const blob = new Blob([file], { type: file.type })
        reader.readAsDataURL(blob)
      } else if (mediaType === ConversationType.TEXT) {
        reader.readAsText(file)
      }
      onFileChange?.(file)
    },
    [onChange],
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!disabled) {
        setIsDragOver(true)
      }
    },
    [disabled],
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      if (disabled) return

      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) {
        handleFile(files[0])
      }
    },
    [disabled, handleFile],
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        handleFile(files[0])
        onFileChange?.(files[0])
      }
    },
    [handleFile, onFileChange],
  )

  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }, [disabled])

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(null)
      onFileChange?.(null)
    },
    [onChange, onFileChange],
  )

  const getAccept = useCallback(() => {
    if (mediaType === ConversationType.IMAGE) return "image/*"
    if (mediaType === ConversationType.AUDIO) return "audio/*"
    if (mediaType === ConversationType.VIDEO) return "video/*"
    if (mediaType === ConversationType.TEXT) return "text/*"
    return "image/*"
  }, [mediaType])

  const getAcceptExtensions = useCallback(() => {
    if (mediaType === ConversationType.IMAGE) return "PNG, JPG up to 5MB"
    if (mediaType === ConversationType.AUDIO) return "MP3, WAV, OGG up to 50MB"
    if (mediaType === ConversationType.VIDEO) return "MP4, WEBM, OGG up to 100MB"
    if (mediaType === ConversationType.TEXT) return "TXT up to 50MB"
    return "PNG, JPG up to 5MB"
  }, [mediaType])

  return (
    <div
      className={twMerge(`relative`, className)}
      style={{ height: typeof hFull === "string" ? `${hFull}` : hFull ? "100" : undefined }}
    >
      <div
        className={twMerge(
          `
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-200
            ${isDragOver ? "border-legacy-blue bg-legacy-blue/5" : "border-legacy-light hover:border-legacy-blue hover:bg-legacy-blue/5"}
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}
            ${value ? "!border-solid border-legacy-blue bg-legacy-blue/5" : ""}
          `,
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        style={{ height: typeof hFull === "string" ? `${hFull}` : hFull ? "100" : undefined }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={getAccept()}
          onChange={handleFileInput}
          className="hidden"
          disabled={disabled}
        />

        {value ? (
          <div
            className={twMerge("relative")}
            style={{
              height: typeof hFull === "string" ? `calc(${hFull} - 48px)` : hFull ? "100" : undefined,
            }}
          >
            {mediaType === ConversationType.IMAGE && (
              <img
                src={value}
                alt="Profile preview"
                className={twMerge("w-full h-32 object-cover rounded-lg")}
                style={{
                  height: typeof hFull === "string" ? `calc(${hFull} - 48px)` : hFull ? "100" : undefined,
                }}
              />
            )}
            {mediaType === ConversationType.AUDIO && (
              <audio src={value} controls className="w-full h-32 rounded-lg" />
            )}
            {mediaType === ConversationType.VIDEO && (
              <video src={value} controls className="w-full h-32 rounded-lg" />
            )}
            {mediaType === ConversationType.TEXT && (
              <div className="w-full h-32 rounded-lg text-xs text-start whitespace-pre-wrap overflow-hidden">
                {value}
              </div>
            )}
            {!disabled && (
              <button
                type="button"
                onClick={handleRemove}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-lg"
                title="Remove photo"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
              Click to change
            </div>
          </div>
        ) : (
          <div
            className={twMerge("space-y-3", !!hFull && "h-full flex flex-col items-center justify-center")}
          >
            {isUploading ? (
              <div className="flex flex-col items-center space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-legacy-blue"></div>
                <p className="text-sm text-legacy-light">Uploading...</p>
              </div>
            ) : (
              <>
                <div className="mx-auto w-12 h-12 bg-legacy-light rounded-full flex items-center justify-center">
                  <Upload className="h-6 w-6 text-legacy-dark" />
                </div>
                <div>
                  <p className="text-sm font-medium text-legacy-dark">
                    {isDragOver ? "Drop image here" : "Upload a photo"}
                  </p>
                  <p className="text-xs text-legacy-light mt-1">Drag & drop or click to select</p>
                  <p className="text-xs text-legacy-light">{getAcceptExtensions()}</p>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PhotoUpload
